"use client";

import { useEffect } from "react";
import ErrorPageView from "./ErrorPageView";
import type { ErrorPageContent } from "@/lib/content/entries/error-pages";
import type { ErrorPageRecovery } from "@/lib/error-pages/recovery-routes";
import { DEVICE_SHELL_ATTRIBUTE, isDeviceShell, type DeviceShell } from "@/lib/device/shell";

type ErrorPageShellProps = {
  readonly content: ErrorPageContent;
  readonly skipLabel: string;
  readonly onRetry?: () => void;
  readonly digest?: string;
  readonly includeDocumentMetadata?: boolean;
  readonly deviceShell?: DeviceShell;
  readonly recovery?: ErrorPageRecovery;
};

const motionProfileByShell: Record<DeviceShell, string> = {
  desktop: "desktop",
  tablet: "touch-reveal",
  mobile: "touch-reveal",
  tv: "remote-focus",
  "4k": "large-canvas",
  watch: "glance-static",
  ereader: "eink-static",
  car: "glance-static",
  xr: "xr-comfort",
  foldable: "touch-reveal",
  "smart-display": "far-view",
  console: "remote-focus",
};

function normalizeDeviceShell(shell: string | null | undefined): DeviceShell {
  return isDeviceShell(shell) ? shell : "desktop";
}

export default function ErrorPageShell({
  content,
  skipLabel,
  onRetry,
  digest,
  includeDocumentMetadata = false,
  deviceShell,
  recovery,
}: ErrorPageShellProps) {
  const initialShell = normalizeDeviceShell(deviceShell);

  useEffect(() => {
    const bodyShell = normalizeDeviceShell(document.body.getAttribute(DEVICE_SHELL_ATTRIBUTE) ?? deviceShell);
    const main = document.getElementById("main-content");

    main?.setAttribute(DEVICE_SHELL_ATTRIBUTE, bodyShell);
    main?.setAttribute("data-seoh-xp-shell", bodyShell);
    main?.setAttribute("data-seoh-xp-motion-profile", motionProfileByShell[bodyShell]);

    const selector = '.skip-link[href="#main-content"]';
    const focusMain = (updateHash = false) => {
      const target = document.getElementById("main-content");

      if (!target) {
        return;
      }

      if (!target.hasAttribute("tabindex")) {
        target.setAttribute("tabindex", "-1");
      }

      document.querySelectorAll("[data-seoh-hash-focus-target]").forEach((element) => {
        if (element !== target) element.removeAttribute("data-seoh-hash-focus-target");
      });
      target.setAttribute("data-seoh-hash-focus-target", "true");

      if (updateHash && window.location.hash !== "#main-content") {
        window.history.pushState(window.history.state, "", "#main-content");
      }

      target.scrollIntoView({ block: "start", inline: "nearest" });
      requestAnimationFrame(() => target.focus({ preventScroll: true }));
    };
    const isSpaceKey = (event: KeyboardEvent) => event.key === " " || event.key === "Spacebar" || event.code === "Space";
    const handleClick = (event: MouseEvent) => {
      const target = event.target;

      if (target instanceof Element && target.closest(selector)) {
        setTimeout(() => focusMain(false), 0);
      }
    };
    const handleKeydown = (event: KeyboardEvent) => {
      const isSpace = isSpaceKey(event);

      if (
        (event.key === "Enter" || isSpace) &&
        document.activeElement instanceof Element &&
        document.activeElement.matches(selector)
      ) {
        if (isSpace) {
          event.preventDefault();
          focusMain(true);
          return;
        }

        setTimeout(() => focusMain(false), 0);
      }
    };
    const handleKeyup = (event: KeyboardEvent) => {
      if (
        isSpaceKey(event) &&
        document.activeElement instanceof Element &&
        document.activeElement.matches(selector)
      ) {
        event.preventDefault();
        focusMain(true);
      }
    };

    if (window.location.hash === "#main-content") {
      focusMain();
    }

    document.addEventListener("click", handleClick, true);
    document.addEventListener("keydown", handleKeydown, true);
    document.addEventListener("keyup", handleKeyup, true);

    return () => {
      document.removeEventListener("click", handleClick, true);
      document.removeEventListener("keydown", handleKeydown, true);
      document.removeEventListener("keyup", handleKeyup, true);
    };
  }, [deviceShell]);

  return (
    <>
      <a href="#main-content" className="skip-link">
        {skipLabel}
      </a>
      <main
        id="main-content"
        className="marketing-main error-page-shell"
        tabIndex={-1}
        data-seoh-xp-surface="marketing"
        data-seoh-xp-state="static"
        data-seoh-xp-intensity="static"
        data-seoh-xp-motion-profile={motionProfileByShell[initialShell]}
        data-seoh-xp-shell={initialShell}
        {...{ [DEVICE_SHELL_ATTRIBUTE]: initialShell }}
      >
        <ErrorPageView
          content={content}
          digest={digest}
          includeDocumentMetadata={includeDocumentMetadata}
          onRetry={onRetry}
          recovery={recovery}
        />
      </main>
    </>
  );
}
